import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, TrendingUp, TrendingDown, BookOpen, LogIn } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal, { SectionLabel } from "../components/Reveal";
import { DeleteButton } from "./admin/ui";

const inputCls = "mt-2 w-full rounded-xl border border-line bg-ink px-3.5 py-2.5 text-sm text-white outline-none transition-colors focus:border-signal";

function Stat({ label, value, tone = "text-white" }) {
  return (
    <div className="card-violet px-5 py-4">
      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted">{label}</p>
      <p className={`mt-1.5 font-mono text-xl font-700 ${tone}`}>{value}</p>
    </div>
  );
}

export default function Journal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trades, setTrades] = useState(null);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), symbol: "", direction: "Long", pnl: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/journal").then(({ data }) => setTrades(data)).catch(() => setTrades([]));

  useEffect(() => {
    if (user) load();
  }, [user]);

  const stats = useMemo(() => {
    if (!trades || !trades.length) return null;
    const wins = trades.filter((t) => t.pnl > 0);
    const losses = trades.filter((t) => t.pnl < 0);
    const net = trades.reduce((s, t) => s + t.pnl, 0);
    return {
      count: trades.length,
      winRate: Math.round((wins.length / trades.length) * 100),
      net,
      avgWin: wins.length ? wins.reduce((s, t) => s + t.pnl, 0) / wins.length : 0,
      avgLoss: losses.length ? losses.reduce((s, t) => s + t.pnl, 0) / losses.length : 0,
    };
  }, [trades]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/journal", { ...form, pnl: parseFloat(form.pnl) });
      toast.success("TRADE LOGGED");
      setForm({ date: new Date().toISOString().slice(0, 10), symbol: "", direction: "Long", pnl: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t) => {
    try {
      await api.delete(`/journal/${t.id}`);
      toast.success("TRADE REMOVED");
      setTrades((prev) => prev.filter((x) => x.id !== t.id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (user === null)
    return (
      <div><Header /><div className="mx-auto max-w-7xl px-5 py-32 font-mono text-xs uppercase tracking-[0.25em] text-muted">CHECKING MEMBERSHIP<span className="animate-blink">…</span></div></div>
    );

  if (user === false)
    return (
      <div data-testid="journal-locked">
        <Header />
        <div className="relative flex min-h-[70vh] items-center justify-center overflow-hidden px-5">
          <div className="terminal-grid absolute inset-0 opacity-50" />
          <div className="card-violet relative max-w-md p-10 text-center">
            <BookOpen size={28} className="mx-auto text-signal" />
            <h1 className="mt-5 font-display text-3xl font-800 tracking-tight text-white">Access Your Journal</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted">Sign in to open your trade records. Your journal is private to your club membership.</p>
            <button onClick={() => navigate("/auth")} data-testid="journal-signin-btn"
              className="btn-beam btn-gradient mt-7 inline-flex items-center gap-2 px-8 py-3.5 font-display text-sm font-800 text-white">
              <LogIn size={14} /> Sign In / Join Free
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );

  return (
    <div data-testid="journal-page">
      <Header />
      <section className="relative overflow-hidden border-b border-line">
        <div className="terminal-grid absolute inset-0 opacity-50" />
        <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-signal/15 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <Reveal>
            <SectionLabel>Member Tool</SectionLabel>
            <h1 className="mt-3 font-display text-4xl font-900 tracking-tight text-white sm:text-5xl">
              Trading <span className="text-gradient">Journal</span>
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted">
              Log every trade, watch your win rate and keep the consistency rule honest. Signed in as{" "}
              <span className="text-white">{user.name}</span>.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
        {stats && (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="journal-stats">
            <Stat label="Trades Logged" value={stats.count} />
            <Stat label="Win Rate" value={`${stats.winRate}%`} tone={stats.winRate >= 50 ? "text-profit" : "text-gold"} />
            <Stat label="Net P&L" value={`${stats.net >= 0 ? "+" : ""}$${stats.net.toFixed(2)}`} tone={stats.net >= 0 ? "text-profit" : "text-loss"} />
            <Stat label="Avg Win" value={`+$${stats.avgWin.toFixed(2)}`} tone="text-profit" />
            <Stat label="Avg Loss" value={`$${stats.avgLoss.toFixed(2)}`} tone="text-loss" />
          </div>
        )}

        <div className="mt-10 grid gap-8 lg:grid-cols-[380px_1fr]">
          <form onSubmit={submit} className="card-violet h-fit p-7" data-testid="trade-form">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Log A Trade"}</p>
            <div className="mt-5 grid grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Date</label>
                <input type="date" required data-testid="trade-date-input" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Symbol</label>
                <input required maxLength={30} data-testid="trade-symbol-input" value={form.symbol}
                  onChange={(e) => setForm({ ...form, symbol: e.target.value })} className={inputCls} placeholder="EURUSD" />
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Direction</label>
                <select data-testid="trade-direction-select" value={form.direction}
                  onChange={(e) => setForm({ ...form, direction: e.target.value })} className={inputCls}>
                  <option>Long</option>
                  <option>Short</option>
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">P&L ($)</label>
                <input required type="number" step="0.01" data-testid="trade-pnl-input" value={form.pnl}
                  onChange={(e) => setForm({ ...form, pnl: e.target.value })} className={inputCls} placeholder="-150 or 320" />
              </div>
            </div>
            <div className="mt-4">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Notes</label>
              <textarea rows={2} maxLength={500} data-testid="trade-notes-input" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className={`${inputCls} resize-none`} placeholder="Setup, emotion, lesson…" />
            </div>
            <button type="submit" disabled={busy} data-testid="trade-submit-btn"
              className="btn-beam btn-gradient mt-6 flex w-full items-center justify-center gap-2 px-5 py-3 font-display text-sm font-800 text-white transition-opacity disabled:opacity-50">
              <Plus size={14} /> {busy ? "Logging…" : "Log Trade"}
            </button>
          </form>

          <div>
            <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-muted">
              {trades ? `${trades.length} trades on record` : "Loading…"}
            </p>
            <div className="space-y-3" data-testid="trades-list">
              {trades && trades.length === 0 && (
                <p className="card-violet p-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-muted" data-testid="trades-empty">
                  No trades logged yet — your first entry is one form away.
                </p>
              )}
              {trades && trades.map((t) => (
                <div key={t.id} className="card-violet flex items-center justify-between gap-4 px-5 py-4" data-testid={`trade-${t.id}`}>
                  <div className="flex min-w-0 items-center gap-4">
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${t.direction === "Long" ? "bg-profit/10 text-profit" : "bg-loss/10 text-loss"}`}>
                      {t.direction === "Long" ? <TrendingUp size={17} /> : <TrendingDown size={17} />}
                    </span>
                    <div className="min-w-0">
                      <p className="font-mono text-sm font-700 text-white">{t.symbol} <span className="ml-1 text-[10px] font-500 uppercase text-muted">{t.direction}</span></p>
                      <p className="mt-0.5 truncate font-mono text-[10px] text-muted">{t.date}{t.notes ? ` · ${t.notes}` : ""}</p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-4">
                    <span className={`font-mono text-sm font-700 ${t.pnl >= 0 ? "text-profit" : "text-loss"}`}>
                      {t.pnl >= 0 ? "+" : ""}${t.pnl.toFixed(2)}
                    </span>
                    <DeleteButton testId={`delete-trade-${t.id}`} onConfirm={() => remove(t)} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
